import {
  CapacitySeatFill18Icon,
  TimeFill30Icon,
  WheelchairFill18Icon,
} from "@vygruppen/spor-icon-react";
import { formatTimeHorizonForDisplay } from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/components/MeasureProgress/utils";
import { getEventDurationInHours } from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/durationDisplay";
import DecisionSupportContext from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/DecisionSupportContext";
import { InfrastructureResponse } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/types/responseSchema";
import { FilterCardSelect } from "features/History/Filter/FilterCardSelect";
import { useContext, useMemo } from "react";
import { CheckmarkDropsButton } from "shared/components/CheckmarkDropsButton";
import { InfoIconHoverAlert } from "shared/components/InfoIconHoverAlert";
import { Box, Flex, HStack, Stack, Text, VStack } from "shared/components/Spor";
import { SectionStateContent } from "shared/components/states/SectionStateContent";
import { isNotNullish } from "shared/utils/objectUtils";
import { useTheme } from "styled-components";

const DURATION_OPTIONS = [2, 4, 6, 8] as const;

type TimeHorizonProps = {
  event: InfrastructureResponse;
};

export const TimeHorizon = ({ event }: TimeHorizonProps) => {
  const theme = useTheme();

  const {
    timeHorizonHours,
    setTimeHorizonHours,
    horizonFromTime,
    horizonToTime,
    totalAffected,
    affectedWithWheelchair,
    isAffectedPending,
    isAffectedError,
  } = useContext(DecisionSupportContext);

  const isLoading = isAffectedPending;
  const isError = isAffectedError;
  const isEmpty = !(
    isLoading ||
    isError ||
    isNotNullish(totalAffected) ||
    isNotNullish(affectedWithWheelchair)
  );

  const sectionState = useMemo(() => {
    let fetchStatus: "error" | "pending" | "success" = "success";
    if (isLoading) {
      fetchStatus = "pending";
    } else if (isError) {
      fetchStatus = "error";
    }

    return {
      fetchStatus,
      isEmpty,
      emptyReason: "Oisann! Her mangler det data for hendelsen.",
    };
  }, [isLoading, isError, isEmpty]);

  const visibleTimePeriod = useMemo(
    () => formatTimeHorizonForDisplay(horizonFromTime, horizonToTime),
    [horizonFromTime, horizonToTime],
  );

  const eventDuration = useMemo(
    () => getEventDurationInHours(event.durations),
    [event.durations],
  );

  return (
    <VStack
      alignItems="start"
      gap={2}
      backgroundColor={theme.colorBackgroundSecondary}
      padding={2}
      borderRadius="xs"
      height="100%"
      width="270px"
      minWidth="270px"
    >
      <Flex direction="row" gap={4} width="100%" position="relative">
        <Text variant="md" fontWeight="bold">
          Tidshorisont
        </Text>
        <InfoIconHoverAlert iconColor={theme.colorIconSecondary}>
          <Stack gap="2">
            <Text variant="sm">
              Tidshorisont angir hvor langt frem i tid du ønsker å se virkningen
              av bruddet.
            </Text>
            <Text variant="sm">
              Visningen er avhengig av tidshorisonten som er satt av vaktleder.
              Dvs. at tidshorisonten aldri kan bli lengre enn det vaktleder har
              satt som maks tid.
            </Text>
            <Text variant="sm">
              Visningen inkluderer alltid de siste 30 minuttene før valgt
              tidshorisont og oppdateres hvert 15. minutt.
            </Text>
          </Stack>
        </InfoIconHoverAlert>
      </Flex>
      <Box
        backgroundColor={theme.colorBackgroundTertiary}
        p={2}
        mb={8}
        borderRadius="xs"
        width="100%"
      >
        <HStack gap={1} alignItems="center">
          <Box>
            <TimeFill30Icon color={theme.colorSuccess} />
          </Box>
          <Text variant="lg" color={theme.colorSuccess} fontWeight="bold">
            {visibleTimePeriod}
          </Text>
        </HStack>

        <Box mt={1}>
          <FilterCardSelect
            label="Tidshorisont: "
            selectedAmount={`${timeHorizonHours} timer`}
          >
            {DURATION_OPTIONS.map((duration) =>
              duration > eventDuration + 2 ? null : (
                <CheckmarkDropsButton
                  key={duration}
                  checked={timeHorizonHours === duration}
                  onClick={() => setTimeHorizonHours(duration)}
                >
                  {duration} timer
                </CheckmarkDropsButton>
              ),
            )}
          </FilterCardSelect>
        </Box>
      </Box>

      <Box mt="auto" width="100%">
        <SectionStateContent size="sm" state={sectionState}>
          <Text variant="sm">Totalt berørte:</Text>
          <Box
            backgroundColor={theme.colorBackgroundTertiary}
            color={theme.colorTextSecondary}
            p={2}
            mt={1}
            gap={1}
            borderRadius="xs"
            width="100%"
          >
            <HStack alignItems="center" gap={0.5}>
              <CapacitySeatFill18Icon />
              <Text variant="sm" fontWeight="bold">
                {totalAffected}
              </Text>
            </HStack>

            <HStack gap={0.5} alignItems="center">
              <WheelchairFill18Icon />
              <Text variant="sm" fontWeight="bold">
                {affectedWithWheelchair}
              </Text>
            </HStack>
          </Box>
        </SectionStateContent>
      </Box>
    </VStack>
  );
};
